import { axisDate, axisTime, shortDate, dateTime } from "./format";

// Up to this span an intraday chart is "one day" and its ticks are times of day.
// A crypto 1D is a rolling 24 hours, so it crosses midnight; the slack covers that.
const ONE_DAY_SPAN_MS = 26 * 60 * 60 * 1000;

/**
 * How a chart's time axis reads, from its points and whether they are intraday.
 * One decision for every chart with a range selector, so 1D/1W/1M label the same
 * way on the instrument page, the dashboard and analytics.
 *
 *   daily points        dailyTick on the axis, the date in the tooltip (unchanged)
 *   intraday, one day   times on the axis ("09:30"), date + time in the tooltip
 *   intraday, many days one tick per calendar day at that day's first candle —
 *                       letting Recharts pick ticks among 15-minute candles would
 *                       print the same date several times over
 *
 * Returns props to spread: `axis` onto <XAxis>, `tooltip` onto <Tooltip>.
 */
export function timeAxis(data, intraday, { dailyTick = axisDate, key = "date" } = {}) {
  if (!intraday || !data || data.length === 0) {
    return { axis: { tickFormatter: (v) => dailyTick(v) }, tooltip: { labelFormatter: (l) => shortDate(l) }, oneDay: false };
  }
  const first = new Date(data[0][key]).getTime();
  const last = new Date(data[data.length - 1][key]).getTime();
  const tooltip = { labelFormatter: (l) => dateTime(l) };
  if (last - first <= ONE_DAY_SPAN_MS) {
    return { axis: { tickFormatter: (v) => axisTime(v) }, tooltip, oneDay: true };
  }
  const ticks = [];
  let prevDay = null;
  for (const d of data) {
    const day = new Date(d[key]).toDateString();
    if (day !== prevDay) { ticks.push(d[key]); prevDay = day; }
  }
  return { axis: { tickFormatter: (v) => axisDate(v), ticks }, tooltip, oneDay: false };
}

// Decimals for a price axis: an intraday range is often a few euros wide, and
// whole-number ticks would print "255, 256, 256, 257" over it.
export function priceAxisDecimals(lo, hi) {
  const span = Math.abs(hi - lo);
  if (span < 5) return 2;
  if (span < 50) return 1;
  return 0;
}

// Width for a price axis, from its longest label. A fixed 38px fit "255" but cut
// Bitcoin's "84.898" to "4.898" on a phone.
export function axisWidth(labels, fontPx, min) {
  const longest = Math.max(0, ...labels.map((l) => String(l).length));
  return Math.max(min, Math.ceil(longest * fontPx * 0.62) + 6);
}
